import {
    actions,
    ActionTypes,
    assign,
    createMachine,
    DoneInvokeEvent,
    EventObject,
    ExtractEvent,
    LogAction,
    send,
    sendParent,
} from "xstate"
import * as E from "fp-ts/Either"
import { Either } from "fp-ts/Either"
import * as F from "fp-ts/function"
import { PathReporter } from "io-ts/PathReporter"
import { Errors } from "io-ts"
import { BadRequestResponse, BadRequestResponseData } from "@ssc-booking/api-contracts"
import { AType, Materialized, summon } from "@silversea-ssc/functional-support"
import { NonEmptyString } from "@ssc-booking/functional-support"
import { assertEvent } from "../../xstate-helpers"
import { Config, DataLoadEvents, DataLoadStateMachine, loadFatalError, retryDataLoadTypes } from "./types"
import { applyPolicy, limitRetries, startStatus } from "./retry-policy"

export type RequestResponseChannel = {
    sendAndReceive(message: Record<string, unknown>): Promise<Record<string, unknown>>
}

export const retryDataLoadMachine = <EReq, AReq, ESuccess, ASuccess, EError, AError, TParentEvent extends EventObject>(
    config: Config<EReq, AReq, ESuccess, ASuccess, EError, AError, TParentEvent>,
): DataLoadStateMachine<AReq, ASuccess, AError> => {
    const {
        id: maybeId,
        channel,
        Request,
        Success,
        successType,
        Error,
        errorType,
        action,
        notifyParent,
        disableLog,
        shouldRetry: maybeShouldRetry,
        retryPolicy,
    } = config
    const id = maybeId ?? action
    const shouldRetry = maybeShouldRetry ?? F.constFalse
    const neverRetry = limitRetries(0)
    const applyRetryPolicy = applyPolicy(retryPolicy ?? neverRetry)
    const types = retryDataLoadTypes(Request, Success, Error)
    type Types = typeof types
    type Context = Types["Context"]
    type Events = Types["Events"]
    type TypeState = Types["TypeState"]
    type LoadEvent = Types["LoadSuccess"] | Types["LoadError"] | Types["LoadFatalError"]
    const logIfEnabled = (logAction: LogAction<Context, Events>) =>
        actions.pure<Context, Events>(() => (disableLog ? undefined : logAction))
    const _parseResponse = parseResponse(Success, Error, successType, errorType)
    const checkShouldRetry = (event: LoadEvent) => {
        if (shouldRetry(event)) return { type: "RETRY" as const }
        return event
    }

    return createMachine<Context, Events, TypeState>(
        {
            id: `data-load-${id}`,
            initial: "idle",
            predictableActionArguments: true,
            context: { id, retryStatus: startStatus },
            states: {
                idle: {
                    on: { LOAD: "loading" },
                },
                loading: {
                    entry: ["clearError", "notifyParent", "setLastRequest"],
                    invoke: {
                        src: "sendRequest",
                        onDone: {
                            actions: "sendResponse",
                        },
                        onError: {
                            actions: "sendFatalError",
                        },
                    },
                    on: {
                        LOAD: { target: "loading", actions: ["resetRetryStatus"] },
                        LOAD_SUCCESS: { target: "loaded", actions: ["setResponse"] },
                        LOAD_ERROR: { target: "error", actions: ["setError"] },
                        LOAD_FATAL_ERROR: { target: "error", actions: ["setFatalError"] },
                        RETRY: { target: "retry" },
                    },
                },
                loaded: {
                    entry: "notifyParent",
                    on: { LOAD: { target: "loading", actions: ["resetRetryStatus"] } },
                },
                error: {
                    entry: ["logError", "notifyParent"],
                    on: { LOAD: { target: "loading", actions: ["resetRetryStatus"] } },
                },
                retry: {
                    entry: ["checkRetry"],
                    on: {
                        LOAD: { target: "loading", actions: ["resetRetryStatus"] },
                        RETRY_LOAD: "loading",
                        LOAD_FATAL_ERROR: { target: "error", actions: ["setFatalError"] },
                    },
                },
            },
        },
        {
            actions: {
                sendResponse: send<Context, Events>((context, event) =>
                    F.pipe(
                        //keep new line
                        asserDoneInvokeEvent(event, ActionTypes.DoneInvoke),
                        _parseResponse,
                        checkShouldRetry,
                    ),
                ),
                sendFatalError: send<Context, Events, Events>((_, event) =>
                    //NOTICE currently the only chance to get here is when the WS closes. Therefore, this case can be
                    // interpreted as "WS closed" (see WebSocketClient).
                    // In case this would change, we should review how we send the info to data-load-machine clients.
                    F.pipe(
                        asserDoneInvokeEvent(event, ActionTypes.ErrorPlatform),
                        connectionCloseResponseEvent,
                        checkShouldRetry,
                    ),
                ),
                setResponse: assign<Context, Events>({
                    response: (_, ev) => {
                        assertEvent(ev, "LOAD_SUCCESS")
                        return ev.response
                    },
                }),
                notifyParent: actions.pure<Context, Events>((ctx, ev) => {
                    const parentEvent = notifyParent(ctx, ev)
                    return parentEvent ? sendParent(parentEvent) : undefined
                }),
                setError: assign<Context, Events>({
                    error: (_, ev) => {
                        assertEvent(ev, "LOAD_ERROR")
                        return { type: "ERROR", error: ev.response }
                    },
                }),
                setFatalError: assign<Context, Events>({
                    error: (_, ev) => {
                        assertEvent(ev, "LOAD_FATAL_ERROR")
                        return { type: "FATAL", errorCode: ev.errorCode, message: ev.message, info: ev.info }
                    },
                }),
                clearError: assign<Context, Events>({
                    error: undefined,
                }),
                setLastRequest: assign<Context, Events>((ctx, ev) => ({
                    lastCommand: ev.type === "LOAD" || ev.type === "RETRY_LOAD" ? ev : undefined,
                })),
                checkRetry: actions.pure<Context, Events>((ctx) => {
                    assertLastCommandAvailable<AReq>(ctx.lastCommand)
                    const retryStatus = applyRetryPolicy(ctx.retryStatus)
                    if (retryStatus.previousDelay === undefined) {
                        return [
                            assign<Context, Events>({ retryStatus }),
                            send<Context, Events>(
                                loadFatalError(
                                    "retriesExceeded",
                                    "Unable to satisfy request in the given retries attempts",
                                    {
                                        oldRetryStatus: ctx.retryStatus,
                                        newRetryStatus: retryStatus,
                                    },
                                ),
                            ),
                        ]
                    }
                    return [
                        assign<Context, Events>({ retryStatus }),
                        send<Context, Events>(
                            { ...ctx.lastCommand, type: "RETRY_LOAD" },
                            { delay: retryStatus.previousDelay },
                        ),
                    ]
                }),
                resetRetryStatus: assign<Context, Events>({ retryStatus: startStatus }),
                logMe: logIfEnabled(actions.log<Context, Events>((ctx, ev) => ({ type: ev.type, ctx, ev }))),
                logError: logIfEnabled(
                    actions.log<Context, Events>((ctx, ev) => ({ id, type: "LOADING ERROR", ctx, ev })),
                ),
            },
            services: {
                sendRequest: (context, event) => {
                    assertEvent(event, "LOAD", "RETRY_LOAD")
                    return channel.sendAndReceive({
                        country: event.country,
                        action: action,
                        data: Request.strictType.encode(event.data),
                    })
                },
            },
        },
    )
}

const loadSuccessEvent = <ASuccess>(response: ASuccess) => ({ type: "LOAD_SUCCESS" as const, response })
const loadErrorEvent = <AError>(response: AError) => ({ type: "LOAD_ERROR" as const, response })
const parseErrorEvent = (event: DoneInvokeEvent<unknown>) => (error: Errors) =>
    loadFatalError("parsing", PathReporter.report(E.left(error)).join("\n"), event.data)
const badRequestResponseEvent = (event: DoneInvokeEvent<unknown>) => (data: BadRequestResponseData) =>
    loadFatalError(data.errorCode, `${BadRequestResponse.strictType.name}: ${data.message}`, event.data)
const connectionCloseResponseEvent = (ev: DoneInvokeEvent<unknown>) =>
    loadFatalError("connectionClosed", `${ev.data}`, ev.data)

const DoneInvokeData = summon((F) => F.interface({ type: NonEmptyString(F), data: F.unknown() }, "DoneInvokeData"))
type DoneInvokeData = AType<typeof DoneInvokeData>

const asserDoneInvokeEvent = <AReq, ASuccess, AError>(
    event: DataLoadEvents<AReq, ASuccess, AError>,
    prefix: string,
) => {
    if (!event.type.startsWith(prefix)) {
        throw new Error(`Invariant violation: Expected event "${prefix}" but instead got "${event.type}"`)
    }
    return event as DoneInvokeEvent<unknown>
}
function assertLastCommandAvailable<AReq>(
    ev: ExtractEvent<DataLoadEvents<AReq, unknown, unknown>, "LOAD" | "RETRY_LOAD"> | undefined,
): asserts ev is ExtractEvent<DataLoadEvents<AReq, unknown, unknown>, "LOAD" | "RETRY_LOAD"> {
    if (!ev) throw new Error("Missing last command")
}

const parseSuccess =
    <ESuccess, ASuccess>(Success: Materialized<ESuccess, ASuccess>) =>
    (data: DoneInvokeData) =>
        F.pipe(Success.strictType.decode(data.data), E.map(loadSuccessEvent))
const parseError =
    <EError, AError>(Error: Materialized<EError, AError>) =>
    (data: DoneInvokeData) =>
        F.pipe(Error.strictType.decode(data.data), E.map(loadErrorEvent))
const parseBadRequestResponse = (data: DoneInvokeData, ev: DoneInvokeEvent<unknown>) =>
    F.pipe(BadRequestResponseData.strictType.decode(data.data), E.map(badRequestResponseEvent(ev)))
const parseResponse = <ESuccess, ASuccess, EError, AError>(
    Success: Materialized<ESuccess, ASuccess>,
    Error: Materialized<EError, AError>,
    maybeSuccessType?: string,
    maybeErrorType?: string,
) => {
    type LoadEvent = ExtractEvent<
        DataLoadEvents<unknown, ASuccess, AError>,
        "LOAD_SUCCESS" | "LOAD_ERROR" | "LOAD_FATAL_ERROR"
    >
    const _parseSuccess = parseSuccess(Success)
    const _parseError = parseError(Error)
    const successType = maybeSuccessType ?? Success.strictType.name
    const errorType = maybeErrorType ?? Error.strictType.name
    return (ev: DoneInvokeEvent<unknown>) =>
        F.pipe(
            DoneInvokeData.strictType.decode(ev.data),
            E.chain((data): Either<Errors, LoadEvent> => {
                if (data.type === successType) return _parseSuccess(data)
                if (data.type === errorType) return _parseError(data)
                if (data.type === BadRequestResponse.strictType.name) return parseBadRequestResponse(data, ev)
                return E.left([{ value: data.type, context: [], message: "Unknown response type" }])
            }),
            E.getOrElseW(parseErrorEvent(ev)),
        )
}
